import { signal } from '@angular/core';
import queryString from 'query-string';
import { ReplaySubject } from 'rxjs';
import { CanvasRendererBrandingOptions, CanvasRendererContentLoadOptions, CanvasRendererHostCallResult, CanvasRendererRenderParams, PdfPaperSize } from '../services/api.services';
import { AppError } from '../shared/utils';

export const isCanvasRendererHost = window.location.pathname.toLowerCase() == '/canvas-renderer';
export const canvasRendererHostToken = queryString.parse(window.location.search)['canvas_renderer_host_token'];
export const canvasRendererHostContentFormat = queryString.parse(window.location.search)['file_type'];

if (canvasRendererHostToken)
  document.cookie = [...document.cookie.split(';'), `canvasRendererHostToken=${canvasRendererHostToken}`].join(';');
  
export const cancellationRequested = new class {
  value = false;
  readonly signal = signal(false);
  readonly observable = new ReplaySubject<boolean>(1);

  private canThrow = false;

  set() {
    if (this.value)
      return;

    this.value = true;
    this.signal.set(true);
    this.observable.next(true);
    this.observable.complete();

    // throw only after a delay (to give some time to cancel any queries in flight)
    setTimeout(() => {
      this.canThrow = true;
    }, 100);
  }

  throwWhenSet() {
    if (this.canThrow)
      throw new Error('cancellation requested');
  }
};

export class InsightsCanvasRendererHostBridge {
  private component: any;
  private isReady = false;
  private contentIsReady = false;
  private error: string = null;

  renderParams: CanvasRendererRenderParams;

  //#region Host Calls
  async requestCancellation(): Promise<void> {
    cancellationRequested.set();
  }

  async waitUntilReady(): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.waitUntilReady`);

    return await waitUntil(() => this.isReady, async () => <CanvasRendererHostCallResult>{
      success: true
    });
  }

  async waitFor(durationSec: number): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.waitFor ${durationSec}s`);

    let done = false;

    setTimeout(() => done = true, durationSec * 1000);

    return await waitUntil(() => done, async () => <CanvasRendererHostCallResult>{
      success: true
    });
  }

  async setRenderParams(renderParams: CanvasRendererRenderParams): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.setRenderParams`, renderParams);
    
    try {
      this.renderParams = renderParams;

      this.setInjectedStyle(createPrintCss(renderParams));

      await this.component.__onRenderParamsChanged(renderParams);
    }
    catch (err) {
      return { success: false, error: unwrapError(err) };
    }

    return { success: true };
  }

  async setBranding(brandingOptions: CanvasRendererBrandingOptions): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.setBranding`, brandingOptions);

    try {
      await this.component.__setBranding(brandingOptions);
    }
    catch (err) {
      return { success: false, error: unwrapError(err) };
    }

    return { success: true };
  }

  async setViewport(width: number, height: number): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.setViewport`, { width, height });

    document.body.style.width = `${width}px`;
    document.body.style.height = `${height}px`;

    return { success: true };
  }

  async loadContent(options: CanvasRendererContentLoadOptions): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.loadContent`, options);

    try {
      this.contentIsReady = false;

      await this.component.__loadContent(options);
    }
    catch (err) {
      return { success: false, error: unwrapError(err) };
    }

    return { success: true };
  }

  async waitUntilContentReady(): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.waitUntilContentReady`);

    return await waitUntil(() => this.contentIsReady, async () => (<CanvasRendererHostCallResult>{
      success: !this.error,
      error: this.error
    }));
  }

  async triggerBreakpoint(): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.triggerBreakpoint`);

    debugger;

    return {
      success: true
    };
  }

  async beforeRender(): Promise<CanvasRendererHostCallResult> {
    console.debug(`InsightsCanvasRendererHostBridge.beforeRender`);
    
    return await this.waitFor(1);
  }
  //#endregion

  setIsReady(component: any) {
    console.debug(`InsightsCanvasRendererHostBridge.setIsReady`, component);

    this.component = component;

    this.isReady = true;
  }

  setContentIsReady(ready: boolean, error?: string) {
    console.debug(`InsightsCanvasRendererHostBridge.setContentIsReady`, { ready, error });

    if (ready) {
      debugger;
      this.contentIsReady = true;
      this.error = error;
    }
    else {
      this.contentIsReady = false;
      this.error = null;
    }
  }


  private injectedStyle: HTMLStyleElement;

  private setInjectedStyle(style: string) {
    if (!this.injectedStyle) {
      this.injectedStyle = document.createElement('style');

      document.head.append(this.injectedStyle);
    }

    this.injectedStyle.innerHTML = style;
  }
};

export async function waitUntil<T>(fn: () => boolean, callbackOrResult?: () => Promise<T>): Promise<T> {
  while (!fn()) {
    cancellationRequested.throwWhenSet();
    await sleep(100);
  }

  cancellationRequested.throwWhenSet();
  let result = await callbackOrResult();

  cancellationRequested.throwWhenSet();
  return result;
}

function sleep(ms: number) {
  return new Promise<void>(resolve => setTimeout(resolve));
}

function createPrintCss(renderParams: CanvasRendererRenderParams) {
  let sizeName: string,
    orientation: string,
    width: number,
    height: number;

  if (renderParams.paperSize === PdfPaperSize.Letter) {
    sizeName = 'Letter';
    width = 10.99;
    height = 8.49;
  }
  else {
    // A4
    sizeName = 'A4';
    width = 11.689;
    height = 8.269;
  }


  if (renderParams.landscape) {
    orientation = 'landscape';
  }
  else {
    orientation = 'portrait';
    [width, height] = [height, width];
  }

  let unit = 'in';

  if (renderParams.size) {
    width = renderParams.size.width;
    height = renderParams.size.height;
    unit = 'px';
  }

  width *= renderParams.scale;
  height *= renderParams.scale;

  let pageWidth = width;
  let pageHeight = height;

  if (renderParams.landscape) {
    height *= renderParams.pageCount;
  }
  else {
    width *= renderParams.pageCount;
  }
  
  let canvasWidth = width;
  let canvasHeight = height;

  let canvasContentStyle = '';

  if (renderParams.renderType == 'image') {
    // When generating image, we aren't priting the same way as we do for PDF and scrollbars appear due to the padding added at canvas level
    // This removes the padding
    canvasContentStyle = `
      .canvas-renderer__pbi-content > dashboard-embed > .embed-dashboard-container,
      .canvas-renderer__pbi-content > visual-embed > .embed-visual-container {
        width: ${canvasWidth} !important;
        height: ${canvasHeight} !important;
      }
      `;
  }

  if (renderParams.renderType == 'pdf') {
    // There is some weird right offset when rendering PDF, and this helps
    canvasWidth += 0.1 * renderParams.scale;
  }

  return `
  @page {
    size: ${sizeName} ${orientation};
  }

  html, body {
    width: ${width}${unit} !important;
    height: ${height}${unit} !important;
  }

  .canvas-renderer__wrapper {
    width: ${canvasWidth}${unit} !important;
    height: ${canvasHeight}${unit} !important;
  }

  .canvas-renderer__pbi-content {
    width: ${pageWidth}${unit} !important;
    height: ${pageHeight}${unit} !important;
  }

  ${canvasContentStyle}

  @media print {
    @page {
      size: ${sizeName} ${orientation};
    }

    html, body {
      width: ${width}${unit} !important;
      height: ${height}${unit} !important;
    }
  }
  `;
}

export let insightsCanvasRendererHostBridge: InsightsCanvasRendererHostBridge = null;

if (isCanvasRendererHost) {
  insightsCanvasRendererHostBridge = new InsightsCanvasRendererHostBridge();

  (window as any).insightsCanvasRendererHostBridge = insightsCanvasRendererHostBridge;
}

function unwrapError(err: any) {
  if (typeof err === 'string')
    return err;

  if (err instanceof AppError)
    return JSON.stringify(err.toErrorDto());

  if (err instanceof Error)
    return `${err.name ?? 'Error'}: ${err.message}\r\n${err.stack}`;

  return JSON.stringify(err);
}
